import React, { useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { ScrollView, Dimensions, Platform } from 'react-native';

import { ErrorScreen } from '@vfgroup-oneplatform/foundation/Components';
import { ShimmerWrapper } from '@vfgroup-oneplatform/foundation/Components/Shimmer';
import { LightThemeColors, withTheme } from '@vfgroup-oneplatform/foundation/Components/Themes';
import { get as _get, isUndefined as _isUndefined } from 'lodash';
import PropTypes from 'prop-types';

import BillingTestKeys from '../../Utils/BillingTestKeys.js';
import View from '../VfView';

import BarChart from './BarChart';
import styles from './Chart.Styles';
import { ChartLoadingConfig } from './LoadingConfigurations/ChartLoading.config';

const width = Dimensions.get('screen').width;

const scrollToEnd = scrollView => {
  scrollView && scrollView.current && scrollView.current.scrollToEnd({ animated: true });
};

const onBarAnimationEnd = (
  scrollViewPositionRef,
  scrollView,
  { currentActiveIndex, previousActiveIndex, barsData },
) => {
  if (currentActiveIndex === barsData.length - 1 || currentActiveIndex === barsData.length - 2) {
    scrollToEnd(scrollView);
  } else if (
    Platform.OS !== 'ios' &&
    _isUndefined(currentActiveIndex) &&
    !_isUndefined(previousActiveIndex) &&
    !_isUndefined(scrollViewPositionRef.current)
  ) {
    // Reset scroll position to prevent UI issue in android where if you're at the end of a scroll view and you collapse a bar a space will be left at the end till you scroll.
    scrollView.current?.scrollTo({
      ...(scrollViewPositionRef.current || {}),
      animated: true,
    });
  }
};

const handleScrollViewAnimation = (isAnimated, setIsAnimated, scrollView, contentWidth) => {
  if (!isAnimated) {
    setIsAnimated(true);
    scrollView.current.scrollTo({
      x: contentWidth - width - 10,
      animated: true,
    });
  }
};

const Chart = forwardRef(function Chart({ loader, isLoading, isError, onTryAgainPress, data, theme, ...props }, ref) {
  const scrollView = useRef(null);
  const scrollViewPosition = useRef(null);
  const [isAnimated, setIsAnimated] = useState(false);

  const [currentActiveIndex, setCurrentActiveIndex] = useState(undefined);

  useImperativeHandle(ref, () => {
    return {
      closeBillDetails() {
        setCurrentActiveIndex(undefined);
      },
    };
  }, []);

  const Loader = loader ? (
    loader
  ) : (
    <ShimmerWrapper
      isLoading={isLoading}
      loadingConfig={ChartLoadingConfig}
      containerStyle={styles.chartLoadingContainer(theme)}
    />
  );

  if (isLoading) {
    return <View style={styles.loader(theme)}>{Loader}</View>;
  }

  if (isError) {
    return (
      <View style={styles.background}>
        <ErrorScreen
          backgroundStyle={styles.chartErrorBackground}
          iconStyle={styles.chartErrorIconStyle}
          iconName={'ic_warning_mid'}
          text="billing_chart_error_text"
          textButton="billing_chart_error_try_again_button"
          textStyle={styles.chartErrorTextStyle}
          tryButtonContainerStyle={styles.chartErrorTryButtonContainerStyle}
          onPress={onTryAgainPress}
        />
      </View>
    );
  }

  return _get(data, 'length', 0) > 0 ? (
    <ScrollView
      testID={BillingTestKeys.chartView}
      horizontal
      ref={scrollView}
      showsHorizontalScrollIndicator={false}
      onContentSizeChange={contentWidth =>
        handleScrollViewAnimation(isAnimated, setIsAnimated, scrollView, contentWidth)
      }
      style={styles.background(theme)}
      contentContainerStyle={styles.contentContainer}
      onScroll={({ nativeEvent: { contentOffset } }) =>
        (scrollViewPosition.current = contentOffset)
      }
      nestedScrollEnabled={true}
    >
      <BarChart
        {...props}
        data={data}
        onBarAnimationEnd={event => onBarAnimationEnd(scrollViewPosition, scrollView, event)}
        currentActiveIndex={currentActiveIndex}
        setCurrentActiveIndex={setCurrentActiveIndex}
      />
    </ScrollView>
  ) : null;
})

Chart.defaultProps = {
  data: [],
  theme: LightThemeColors,
};

Chart.propTypes = {
  data: PropTypes.array,
  limit: PropTypes.number,
  currency: PropTypes.string,
  limitLabel: PropTypes.string,
  isLoading: PropTypes.bool,
  loader: PropTypes.node,
  isError: PropTypes.bool,
  onTryAgainPress: PropTypes.func,
  theme: PropTypes.object,
};

export { onBarAnimationEnd, scrollToEnd, handleScrollViewAnimation };
export default withTheme(Chart);
